import React, { useContext, useEffect, useState, lazy, Suspense } from "react";
import { Link, Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { OperationsProvider } from "../context/OperationsContext";
import { SlideProvider } from "../context/SlideContext";
import { VideoProvider } from "../context/VideoContext";
import { UserContext } from "../context/UserContext";
import {
  AppBar,
  Toolbar,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Switch,
  CssBaseline,
  Box,
  Typography,
  Button,
  Snackbar,
  IconButton,
  Alert,
} from "@mui/material";
import { Menu, Dashboard, People, ExitToApp, Home, Settings as SettingsIcon, HeadsetMic } from "@mui/icons-material";
import AdminFooter from "./AdminFooter";

const DashboardPage = lazy(() => import("./Dashboard"));
const ManageAyarSettings = lazy(() => import("./ManageAyarSettings"));
const MarqueeSettings = lazy(() => import("./MarqueeSettings"));
const PendingRequests = lazy(() => import("./PendingRequests"));
const PriceUpdateForm = lazy(() => import("./PriceUpdateForm"));
const Settings = lazy(() => import("./Settings"));
const SlideManagement = lazy(() => import("./SlideManagement"));
const VideoManagement = lazy(() => import("./VideoManagement"));
const UserManagement = lazy(() => import("./UserManagement"));
const UserProfile = lazy(() => import("./UserProfile"));
const ContactPage = lazy(() => import("./contanct"));

const AdminPanel = ({
  marqueeText,
  setMarqueeText,
  scrollAmount,
  setScrollAmount,
  onSaveMarqueeSettings,
  symbols,
  isStreamOn,
  isFrozen,
  handleTogglePrices,
  handleFreezePrices,
}) => {
  const { currentUser, logoutUser, setCurrentUser } = useContext(UserContext);
  const navigate = useNavigate();
  const [darkMode, setDarkMode] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [notification, setNotification] = useState({ open: false, message: "" });

  useEffect(() => {
    const storedUser = localStorage.getItem("currentUser");

    if (!currentUser && storedUser) {
      setCurrentUser(JSON.parse(storedUser));
    } else if (!currentUser && !storedUser) {
      setNotification({ open: true, message: "Yetkilendirme hatası: Lütfen giriş yapın." });
      navigate("/login", { replace: true });
    }

    const handleStorageChange = (event) => {
      if (event.key === "currentUser" && !event.newValue) {
        logoutUser();
        navigate("/login", { replace: true });
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [currentUser, logoutUser, navigate, setCurrentUser]);

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  const isExpired =
    currentUser.remainingDays !== undefined && currentUser.remainingDays <= 0;

  const handleLogout = async () => {
    try {
      await logoutUser();
      sessionStorage.removeItem("currentUser");
      const databases = await indexedDB.databases();
      for (const db of databases) {
        try {
          await indexedDB.deleteDatabase(db.name);
        } catch (error) {
          console.warn(`Veritabanı silinemedi: ${db.name}`, error);
        }
      }
      setNotification({ open: true, message: "Başarıyla çıkış yapıldı." });
      setTimeout(() => {
        navigate("/login", { replace: true });
        window.location.reload();
      }, 500);
    } catch (error) {
      console.error("Çıkış hatası:", error);
      setNotification({ open: true, message: "Çıkış sırasında bir hata oluştu. Lütfen tekrar deneyin." });
    }
  };

  const toggleDarkMode = () => {
    setDarkMode((prev) => !prev);
    document.body.classList.toggle("dark-mode", !darkMode);
  };

  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  return (
    <OperationsProvider>
      <SlideProvider>
        <VideoProvider>
        <CssBaseline />
        <Box sx={{ display: "flex" }}>
          <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
            <Toolbar>
              <IconButton color="inherit" onClick={toggleSidebar} edge="start" sx={{ mr: 2 }}>
                <Menu />
              </IconButton>
              <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                Yönetim Paneli
              </Typography>
              <Switch checked={darkMode} onChange={toggleDarkMode} />
              <Button color="inherit" onClick={handleLogout} startIcon={<ExitToApp />}>
                Çıkış Yap
              </Button>
            </Toolbar>
          </AppBar>

          {isExpired && (
            <Alert severity="warning" sx={{ width: "100%", borderRadius: 0 }}>
              Hesap süreniz dolmuş. Lütfen yönetici ile iletişime geçerek aboneliğinizi yenileyin.
            </Alert>
          )}

          {sidebarOpen && (
            <Drawer
              variant="permanent"
              sx={{
                width: 240,
                flexShrink: 0,
                [`& .MuiDrawer-paper`]: { width: 240, boxSizing: "border-box" },
              }}
            >
              <Toolbar />
              <List>
                <ListItem component={Link} to="/admin">
                  <ListItemIcon>
                    <Dashboard />
                  </ListItemIcon>
                  <ListItemText primary="Yönetim Paneli" />
                </ListItem>
                <ListItem component={Link} to="/">
                  <ListItemIcon>
                    <Home />
                  </ListItemIcon>
                  <ListItemText primary="Ana Sayfa" />
                </ListItem>
                {currentUser && ["admin", "moderator"].includes(currentUser.role) && (
                  <>
                    <ListItem component={Link} to="/admin/price-update">
                      <ListItemIcon>
                        <SettingsIcon />
                      </ListItemIcon>
                      <ListItemText primary="Fiyat Güncelleme" />
                    </ListItem>
                    <ListItem component={Link} to="/admin/manage-ayar-settings">
                      <ListItemIcon>
                        <SettingsIcon />
                      </ListItemIcon>
                      <ListItemText primary="Ayarları Yönet" />
                    </ListItem>
                  </>
                )}
                {currentUser && currentUser.role === "admin" && (
                  <>
                    <ListItem component={Link} to="/admin/user-management">
                      <ListItemIcon>
                        <People />
                      </ListItemIcon>
                      <ListItemText primary="Kullanıcı Yönetimi" />
                    </ListItem>
                    <ListItem component={Link} to="/admin/marquee-settings">
                      <ListItemIcon>
                        <SettingsIcon />
                      </ListItemIcon>
                      <ListItemText primary="Kayan Yazı" />
                    </ListItem>
                    <ListItem component={Link} to="/admin/slide-management">
                      <ListItemIcon>
                        <SettingsIcon />
                      </ListItemIcon>
                      <ListItemText primary="Slayt Yönetimi" />
                    </ListItem>
                    <ListItem component={Link} to="/admin/video-management">
                      <ListItemIcon>
                        <SettingsIcon />
                      </ListItemIcon>
                      <ListItemText primary="Video Yönetimi" />
                    </ListItem>
                    <ListItem component={Link} to="/admin/pending-requests">
                      <ListItemIcon>
                        <People />
                      </ListItemIcon>
                      <ListItemText primary="Bekleyen Üyelik Talepleri" />
                    </ListItem>
                  </>
                )}
                <ListItem component={Link} to="/admin/contact">
                  <ListItemIcon>
                    <HeadsetMic />
                  </ListItemIcon>
                  <ListItemText primary="Destek Hattı" />
                </ListItem>
              </List>
            </Drawer>
          )}

          <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
            <Toolbar />
            <Suspense fallback={<div className="loading">Yükleniyor...</div>}>
              <Routes>
                <Route path="/" element={<DashboardPage onTogglePrices={handleTogglePrices} onFreezePrices={handleFreezePrices} />} />
                <Route path="profile/:userId" element={<UserProfile />} />
                {currentUser && ["admin", "moderator"].includes(currentUser.role) && (
                  <>
                    <Route path="price-update" element={<PriceUpdateForm />} />
                    <Route
                      path="manage-ayar-settings"
                      element={<ManageAyarSettings />}
                    />
                  </>
                )}
                {currentUser && currentUser.role === "admin" && (
                  <>
                    <Route path="settings" element={<Settings />} />
                    <Route path="user-management" element={<UserManagement />} />
                    <Route
                      path="marquee-settings"
                      element={
                        <MarqueeSettings
                          marqueeText={marqueeText}
                          setMarqueeText={setMarqueeText}
                          scrollAmount={scrollAmount}
                          setScrollAmount={setScrollAmount}
                          onSave={onSaveMarqueeSettings}
                        />
                      }
                    />
                    <Route path="slide-management" element={<SlideManagement />} />
                    <Route path="video-management" element={<VideoManagement />} />
                    <Route path="pending-requests" element={<PendingRequests />} />
                  </>
                )}
                <Route path="contact" element={<ContactPage />} />
              </Routes>
            </Suspense>
          </Box>
        </Box>
        <Snackbar
          open={notification.open}
          autoHideDuration={3000}
          onClose={() => setNotification({ ...notification, open: false })}
          message={notification.message}
        />
        <AdminFooter />
        </VideoProvider>
      </SlideProvider>
    </OperationsProvider>
  );
};

export default AdminPanel;
